import { useContext, useEffect, useState } from "react";
import BreadCrumb from "../components/breadCrumb";
import DataGrid from "../components/common/DataGrid";
import { TextArea } from "../components/common/TextArea";
import { TextBox } from "../components/common/TextBox";
import PageContent from "../components/pageContent";
import PopupContent from "../components/popupContent";
import {
  apiCreateOrUpdatePigment,
  apiGetAllPigments,
  apiGetPigmentDetail,
} from "../services/PigmentsService";
import {
  apiGetSampleCardDetail,
  apiSampleCardCreateOrUpdate,
} from "../services/SampleCardsService";

import PopupOperationsBar from "./PopupOperationsBar";

import iconOdstiny from "../images/buttons/odstin.png";

import {
  apiColorCreateOrUpdate,
  apiDeleteColorPicture,
  apiGetColorDetail,
  apiUpdatePictureName,
} from "../services/ColorsService";
import { apiUploadFile } from "../services/FilesService";
import { Picture } from "../components/common/Picture";
import { XYZColorSelector } from "../components/common/XYZColorSelector";
import { addNotification } from "../redux/actions";
import { useDispatch } from "react-redux";
import { model } from "../validation/models/color";
import { ValidationProvider } from "../providers/ValidationContext";
import { validateForm } from "../validation/formValidation";
import { useTranslation } from "react-i18next";
import TranslationContext from "../providers/TranslationContext";
import { FormatString } from "../helpers/stringHelpers";
// styles
require("./PopupGeneralStyles.css");

interface Props {
  id?: number;
  handleCancel: any;
  refreshData: any;
  sampleCardId: any;
  sampleCardName: any
}

const ColorPopup = (props: Props) => {
  const isEditMode = props.id ? true : false;

  const translationContext = useContext(TranslationContext) as any;
  const prefix = translationContext.prefix;
  const { t } = useTranslation(undefined, { keyPrefix: `${prefix}.Popup` });
  const cloneDeep = require('clone-deep');
  const [validationModel, setValidationModel] = useState(cloneDeep(model));

  const [loading, setLoading] = useState(true);
  const data: any = {};
  const [formData, setFormData] = useState(data);
  const [saveError, setSaveError] = useState(false);
  const [pictureFile, setPictureFile] = useState<any>();
  const [picturePreview, setPicturePreview] = useState<string>();

  const dispatch = useDispatch();

  useEffect(() => {
    if (props.id) {
      apiGetColorDetail(props.id).then((responseData) => {
        setFormData(responseData.data);

        setLoading(false);
      }).catch((err: any) => {
        dispatch(addNotification({ text: FormatString(t('LoadingDetailsError'), `${props.id}`), type: 'error' }));
        props.handleCancel();
      });
    } else {
      setLoading(false);
    }
  }, []);

  const onAddingClick = () => {
    formData.sampleCardId = props.sampleCardId;
    validateForm(validationModel, formData, isEditMode);
    setValidationModel({ ...validationModel });
    if (validationModel.isValid) {
      setLoading(true);
      apiColorCreateOrUpdate(formData)
        .then((responseData) => {
          props.refreshData();
          props.handleCancel();

          dispatch(addNotification({ text: FormatString(t('CreateSuccess'), formData.name), type: 'success' }));

          setLoading(false);
        })
        .catch((err: any) => {
          dispatch(addNotification({ text: FormatString(t('CreateFailure'), formData.name), type: 'error' }));

          setSaveError(true);
        });
    }
  };

  const handlePictureChange = (e: any) => {
    const selectedFile = e.target.files && e.target.files[0];
    if (!selectedFile) {
      return;
    }
    setPictureFile(selectedFile);
    setPicturePreview(URL.createObjectURL(selectedFile));
  };

  const uploadPicture = async (colorId: number) => {
    if (!pictureFile) {
      return;
    }
    const pictureFormData = new FormData();
    pictureFormData.append('formFile', pictureFile);
    pictureFormData.append('fileName', pictureFile.name);

    await apiUploadFile(pictureFormData);
    await apiUpdatePictureName(colorId, pictureFile.name);
  };

  const onEditingClick = () => {
    validateForm(validationModel, formData, isEditMode);
    setValidationModel({ ...validationModel });
    if (validationModel.isValid) {
      setLoading(true);
      apiColorCreateOrUpdate(formData)
        .then(() => {
          return uploadPicture(props.id as number)
            .catch(() => {
              dispatch(addNotification({ text: FormatString(t('UploadPictureFailure'), pictureFile.name, `${props.id}`), type: 'error' }));
            });
        })
        .then(() => {
          props.refreshData();
          props.handleCancel();

          dispatch(addNotification({ text: FormatString(t('EditSuccess'), formData.name), type: 'success' }));

          setLoading(false);
        })
        .catch((err: any) => {
          dispatch(addNotification({ text: FormatString(t('EditFailure'), formData.name), type: 'error' }));
          setSaveError(true);
        });
    }
  };

  const onCancelClick = () => {
    props.handleCancel();
  };

  const onDeletePictureClick = () => {
    if (!props.id) {
      return;
    }

    setLoading(true);
    apiDeleteColorPicture(props.id)
      .then(() => {
        setFormData({ ...formData, pictureName: null, isPicture: false });
        setPictureFile(undefined);
        setPicturePreview(undefined);
        props.refreshData();

        dispatch(addNotification({ text: FormatString(t('DeletePictureSuccess'), `${props.id}`), type: 'success' }));
      })
      .catch(() => {
        dispatch(addNotification({ text: FormatString(t('DeletePictureFailure'), `${props.id}`), type: 'error' }));
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <PopupContent
      headerIcon={iconOdstiny}
      header={props.id ? t('EditLabel') : t('CreateLabel')}
    >
      <ValidationProvider value={validationModel}>
        <TextBox
          label={t('Labels.sampleCard')}
          defaultValue={props.sampleCardName}
          disabled={true}
          placeholder={t('Labels.sampleCardPlaceholder')}
          dataField="sampleCardName"
        />
        <TextBox
          dataField="name"
          defaultValue={formData.name}
          formData={formData}
          setStateMethod={setFormData}
          id="name"
          label={t('Labels.name')}
          placeholder={t('Labels.namePlaceholder')}
        />

        <TextArea
          id="noteInternal"
          label={t('Labels.noteInternal')}
          placeholder={t('Labels.noteInternalPlaceholder')}
          rows={4}
          dataField="noteInternal"
          value={formData.noteInternal}
          formData={formData}
          setStateMethod={setFormData}
        />

        <XYZColorSelector
          id="xyzColor"
          label={t('Labels.xyzColor')}
          dataField="xyzColor"
          defaultXYZ={formData.xyzColor}
          rgbColorFieldName={'rgbColor'}
          formData={formData}
          placeholder={t('Labels.xyzColorPlaceholder')}
          setStateMethod={setFormData}
        />

        {isEditMode && (
          <div className="row">
            <label htmlFor="picture" className="col-sm-3 col-form-label zvlabel">
              {t('Labels.picture')}
            </label>
            <div className="col-sm-9">
              <div style={{ marginBottom: '5px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                {picturePreview && <img src={picturePreview} alt="" style={{ width: 50, height: 50 }} />}
                {!picturePreview && formData.pictureName && <Picture fileName={formData.pictureName} previewSize={160} />}
                {!picturePreview && !formData.pictureName && <span>{t('Labels.noPicture')}</span>}
                {!picturePreview && formData.pictureName && (
                  <button
                    type="button"
                    className="btn btn-sm btn-danger"
                    onClick={onDeletePictureClick}
                  >
                    {t('DeletePictureButton')}
                  </button>
                )}
              </div>
              <input
                id="picture"
                type="file"
                accept="image/*"
                onChange={handlePictureChange}
              />
            </div>
          </div>
        )}

        <PopupOperationsBar
          handleAdding={onAddingClick}
          handleCancellation={onCancelClick}
          handleEditing={onEditingClick}
          id={props.id}
        />
      </ValidationProvider>
    </PopupContent>
  );
};

export default ColorPopup;
